import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { profileApi, User, ClientProfile, BankAccount } from '../../services/profileApi';

interface ProfileState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

const initialState: ProfileState = {
  user: null,
  loading: false,
  error: null,
};

// Async thunk for fetching profile data
export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileApi.getProfile();
      return response.data.user;
    } catch (error: any) {
      console.error('Profile fetch error:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to load profile'
      );
    }
  }
);

// Async thunk for changing password
export const changePassword = createAsyncThunk(
  'profile/changePassword',
  async (data: { current_password: string; new_password: string; new_password_confirmation: string }, { rejectWithValue }) => {
    try {
      const response = await profileApi.changePassword(data);
      return response;
    } catch (error: any) {
      console.error('Change password error:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to change password'
      );
    }
  }
);

// Async thunk for updating profile
export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async (data: Partial<ClientProfile>, { rejectWithValue }) => {
    try {
      const response = await profileApi.updateProfile(data);
      return response;
    } catch (error: any) {
      console.error('Update profile error:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update profile'
      );
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearProfile: (state) => {
      state.user = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Profile
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Optionally update the user data if the API returns updated user info
        if (action.payload?.user) {
          state.user = action.payload.user;
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearProfile, clearError } = profileSlice.actions;
export default profileSlice.reducer;

// Export types for use in components
export type { User, ClientProfile, BankAccount, ProfileState }; 